import React, { useEffect } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Grid, InputLabel, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useObrasState } from "../context/obras-context";
import { useObrasController } from "../context/obras-controller";
import { ObraFotosSection } from "./obra-fotos-section";
import { SelectWithQuickAdd } from "../../../components/select-with-quick-add";
import { CurrencyInput } from "../../../components/currency-input";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";

export const ObraForm = () => {
    const { dataRequest, situacoesList, funcoesList, tiposList, contratosList, conveniosList, pendingFotos } = useObrasState();
    const { handleOnChangeForm, loadLookups, addPendingFoto, removePendingFoto } = useObrasController();

    useEffect(() => {
        (async () => {
            await loadLookups();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item lg={12} sm={12} xl={12}>
                        <Alert severity="info">
                            Se Contrato ou Convênio ainda não existirem, cadastre-os primeiro no menu lateral antes de preencher esta Obra.
                        </Alert>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Contrato *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idContrato}
                            value={dataRequest?.idContrato ?? ''}
                            onChange={(e) => handleOnChangeForm('idContrato', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {contratosList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Convênio (opcional)</InputLabel>
                        <Select
                            size="small"
                            value={dataRequest?.idConvenio ?? ''}
                            onChange={(e) => handleOnChangeForm('idConvenio', e.target.value === '' ? null : e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Nenhum</em></MenuItem>
                            {conveniosList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Situação *</InputLabel>
                        <Select
                            size="small"
                            error={!dataRequest?.idSituacaoObra}
                            value={dataRequest?.idSituacaoObra ?? ''}
                            onChange={(e) => handleOnChangeForm('idSituacaoObra', e.target.value)}
                            fullWidth
                        >
                            <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                            {situacoesList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <SelectWithQuickAdd
                            label="Função *"
                            error={!dataRequest?.idFuncaoObra}
                            value={dataRequest?.idFuncaoObra ?? ''}
                            onChange={(value) => handleOnChangeForm('idFuncaoObra', value)}
                            options={funcoesList}
                            addLabel="Nova Função de Obra"
                            onAdd={async (nome) => {
                                await AdminPortalAppService.addUpdateFuncaoObra({ nomFuncaoObra: nome });
                                await loadLookups();
                            }}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <SelectWithQuickAdd
                            label="Tipo de Obra *"
                            error={!dataRequest?.idTipoObra}
                            value={dataRequest?.idTipoObra ?? ''}
                            onChange={(value) => handleOnChangeForm('idTipoObra', value)}
                            options={tiposList}
                            addLabel="Novo Tipo de Obra"
                            onAdd={async (nome) => {
                                await AdminPortalAppService.addUpdateTipoObra({ nomTipoObra: nome });
                                await loadLookups();
                            }}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Data de Início *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaInicio } }}
                            value={dataRequest?.dtaInicio ? dayjs(dataRequest.dtaInicio) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaInicio', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Previsão de Conclusão *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: !dataRequest?.dtaPrevisaoConclusao } }}
                            value={dataRequest?.dtaPrevisaoConclusao ? dayjs(dataRequest.dtaPrevisaoConclusao) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaPrevisaoConclusao', value?.toISOString())}
                        />
                    </Grid>

                    <Grid item lg={4} sm={4} xl={4}>
                        <InputLabel>Data de Conclusão (opcional)</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            value={dataRequest?.dtaConclusao ? dayjs(dataRequest.dtaConclusao) : null}
                            onChange={(value: Dayjs | null) => handleOnChangeForm('dtaConclusao', value?.toISOString() ?? null)}
                        />
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Valor da Obra *</InputLabel>
                        <CurrencyInput
                            size="small"
                            error={!dataRequest?.valorObra || dataRequest.valorObra <= 0}
                            value={dataRequest?.valorObra}
                            onChange={(value) => handleOnChangeForm('valorObra', value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item lg={6} sm={6} xl={6}>
                        <InputLabel>Percentual Executado (opcional)</InputLabel>
                        <OutlinedInput
                            size="small"
                            type="number"
                            value={dataRequest?.percentualExecutado ?? ''}
                            onChange={(e) => handleOnChangeForm('percentualExecutado', e.target.value === '' ? null : Number(e.target.value))}
                            fullWidth
                        />
                    </Grid>

                    <ObraFotosSection
                        idObra={dataRequest?.id ?? undefined}
                        pendingFotos={pendingFotos}
                        onAddPending={addPendingFoto}
                        onRemovePending={removePendingFoto}
                    />
                </Grid>
            </LocalizationProvider>
        </React.Fragment>
    );
};
